import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import i18n from 'i18next';
import { useTranslation } from "react-i18next";
import { Collapse, Select } from 'antd';
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from 'html-to-draftjs';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { useParams } from "react-router-dom";

import useAdminApplicationDetailStore from "../../../store/useAdminApplicationDetailStore";
import useModalStore from "../../../store/useModalStore";
import useAuthAdminStore from "../../../store/useAuthAdminStore";
import useLoadingStore from "../../../store/useLoadingStore";
import Flex from "../../core/Flex";
import FormField from "../../form/FormField";
import FormLabel from "../../form/FormLabel";
import FormTitle from "../../form/FormTitle";
import FormText from "../../form/FormText";
import FormRemark from "../../form/FormRemark";
import FormSelect from "../../form/FormSelect";
import FormInput from "../../form/FormInput";
import FormInputError from "../../form/FormInputError";
import FormButtonPrimary from "../../form/FormButtonPrimary";
import FormButtonSecondary from "../../form/FormButtonSecondary";
import Divider from "../../core/Divider";
import Space from "../../core/Space";
import withErrorHandling from "../../../function/withErrorHandling";
import { apiDomain, basename, isProduction } from "../../../setting";
import feWriteLog from "../../../function/feWriteLog";

// Content for Compose Email
export const ContentComposeEmail = ({ showReason = false, disabled = false }) => {
  const { t } = useTranslation();
  const { contentComposeEmail_Template_List, contentComposeEmail_Template, setContentComposeEmail_Template, emailTemplate } = useAdminApplicationDetailStore();
  const { isValidEmailTemplate, setIsValidEmailTemplate } = useAdminApplicationDetailStore();
  const { contentComposeEmail_Reason_List, contentComposeEmail_Reason, setContentComposeEmail_Reason } = useAdminApplicationDetailStore();
  const { contentComposeEmail_Title, setContentComposeEmail_Title } = useAdminApplicationDetailStore();
  const { contentComposeEmail_Content, openEmailTemplate } = useAdminApplicationDetailStore();
  const { editorState, setEditorState } = useAdminApplicationDetailStore();
  const { admSendEmail, emailAttachment, addEmailAttachment, removeEmailAttachment } = useAdminApplicationDetailStore();
  const { isValidEmailTitle, setIsValidEmailTitle } = useAdminApplicationDetailStore();
  const { setLoading } = useLoadingStore();
  const { data } = useAdminApplicationDetailStore();
  const { token, logout } = useAuthAdminStore();
  const { openModalInfo } = useModalStore();

  const attachmentUploadRef = useRef(null);

  const handleFileSelect = async (event, id) => {
    try {
      setLoading(true);
      const file = event.target.files[0];

      const allowedFileTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/gif', 'image/tiff', 'image/tif'];
      const isAllowedFileType = allowedFileTypes.includes(file.type);
      if (!isAllowedFileType) {
        setLoading(false);
        throw new Error('File Type Error');
      }

      const isFileSizeValid = file.size <= 20 * 1024 * 1024; // 20MB in bytes
      if (!isFileSizeValid) {
        setLoading(false);
        throw new Error('File Size Error');
      }

      // console.log("upload file", file, fileList)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('attachid', id);
      formData.append('module', 'PREAUTH');

      const response_upload = await fetch(`${apiDomain}/${basename}/api/general/uploadFile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (response_upload.status === 401) { // Unauthorized
        logout();
        setLoading(false);
        throw new Error('Unauthorized');
      }

      if (!response_upload.ok) {
        setLoading(false);
        throw new Error('Network Error');
      }

      const data_upload = await response_upload.json();

      const { attachid, attachobjid, contenttype, ofilename } = data_upload.attachment;
      const response_download = await fetch(`${apiDomain}/${basename}/api/general/download/${attachobjid}?isthumb=squ`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      if (response_download.status === 401) { // Unauthorized
        logout();
        setLoading(false);
        throw new Error('Unauthorized');
      }

      if (!response_download.ok) {
        setLoading(false);
        throw new Error(`Network Error`);
      }

      const data_download = await response_download.json();

      const response_downloadOrg = await fetch(`${apiDomain}/${basename}/api/general/download/${attachobjid}?isthumb=N`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      if (response_downloadOrg.status === 401) { // Unauthorized
        logout();
        setLoading(false);
        throw new Error('Unauthorized');
      }

      if (!response_downloadOrg.ok) {
        setLoading(false);
        throw new Error(`Network Error`);
      }

      const data_downloadOrg = await response_downloadOrg.json();

      addEmailAttachment({
        attachid: attachid,
        attachobjid: attachobjid,
        name: ofilename,
        type: contenttype,
        url: data_download,
        orgUrl: data_downloadOrg,
      });

      setLoading(false);

    } catch (error) {
      feWriteLog(`Error: ${error.message}`);
      if (error.message === "Unauthorized") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_expiry" : "pre_auth_uat_error_expiry",
          title: t("error:unauthorized.title"),
          text: t("error:unauthorized.message")
        });
        // setStep("loading");
      } else if (error.message === "Network Error") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_network" : "pre_auth_uat_error_network",
          title: t("error:network.title"),
          text: t("error:network.message")
        });
      } else if (error.message === "File Type Error") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_file_type" : "pre_auth_uat_error_file_type",
          title: t("error:fileFormat.title"),
          text: t("error:fileFormat.message")
        });
      } else if (error.message === "File Size Error") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_file_size" : "pre_auth_uat_error_file_size",
          title: t("error:fileSize.title"),
          text: t("error:fileSize.message")
        });
      } else {
        openModalInfo({
          id: isProduction ? "pre_auth_error_unknown" : "pre_auth_uat_error_unknown",
          title: t("error:unknown.title"),
          text: t("error:unknown.message")
        });
      }

      setLoading(false);

      return false;
    }
  };

  const loadEmailTemplate = withErrorHandling(openEmailTemplate);

  useEffect(() => {
    const contentBlock = htmlToDraft(contentComposeEmail_Content);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      const editorState = EditorState.createWithContent(contentState);
      setEditorState(editorState);
    }
  }, [contentComposeEmail_Content]);

  return (
    <Flex gap={24} direction={"column"} alignItems={"flex-start"}>
      <FormText>This email will be sent to : <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span></FormText>

      <Flex width={"100%"} gap={16}>
        <FormField style={{ flex: 1, marginBottom: 0 }}>
          <FormLabel>Email Template</FormLabel>
          <FormSelect
            options={contentComposeEmail_Template_List}
            disabled={disabled}
            value={contentComposeEmail_Template}
            handleSelect={(value) => {
              setContentComposeEmail_Template(value);
              setIsValidEmailTitle(true);
              setIsValidEmailTemplate(true);
              loadEmailTemplate(data?.form?.aid, value.value);
            }}
          ></FormSelect>
          <FormInputError show={!isValidEmailTemplate}>* 請輸入Email Template</FormInputError>
        </FormField>

        {
          (showReason || contentComposeEmail_Template.value === "DECLINEDLOG" || contentComposeEmail_Template.value === "DECLINEDPCA" || contentComposeEmail_Template.value === "CHGPCA") &&
          <FormField style={{ flex: 1, marginBottom: 0 }}>
            <FormLabel>Reason</FormLabel>
            <Select
              mode="multiple"
              size={"large"}
              maxCount={3}
              allowClear
              optionFilterProp="label"
              options={contentComposeEmail_Reason_List.map((item) => {
                return {
                  label: `${item.value} - ${item.label}`,
                  value: item.value,
                }
              })}
              value={contentComposeEmail_Reason}
              onChange={(value) => {
                setContentComposeEmail_Reason(value);
                loadEmailTemplate(data?.form?.aid, contentComposeEmail_Template.value);
              }}
              style={{
                width: "100%",
              }}
            />
          </FormField>
        }
      </Flex>

      <Flex width={"100%"} gap={16} alignItems={"flex-start"}>
        <FormField style={{ width: "100%", marginBottom: 0 }}>
          <FormLabel htmlFor="email_title">Email Title</FormLabel>
          <FormInput id="email_title" value={contentComposeEmail_Title} handleChange={e => {
            setContentComposeEmail_Title(e.target.value);

            if (e.target.value.length > 0) {
              setIsValidEmailTitle(true);
            } else {
              setIsValidEmailTitle(false);
            }
          }} placeholder="請輸入Email Title" aria-label="Email Title" />
          <FormInputError show={!isValidEmailTitle}>* 請輸入Email Title</FormInputError>
        </FormField>

        <input type="file" style={{ display: 'none' }} onChange={(event) => {
          handleFileSelect(event, emailTemplate?.email?.emailId);
        }} ref={attachmentUploadRef} />
        <AttachmentBtn onClick={() => attachmentUploadRef.current.click()}>
          <Icon24 src={`${process.env.PUBLIC_URL}/images/document-shield-link.png`} alt="Attachment" />
          <FormText style={{ color: "var(--aia-bx-dark-blue)", fontWeight: "bold" }}>Attachment</FormText>
        </AttachmentBtn>
      </Flex>

      {
        emailAttachment.length > 0 && <Flex gap={16} style={{ flexWrap: "wrap" }}>
          {
            emailAttachment.map((item, index) => {
              return (
                <AttachmentItem key={item.attachobjid}>
                  <AttachmentFile href={item.orgUrl} target="_blank" rel="noopener noreferrer" download={item.name} title={item.name}>
                    <Icon24 src={`${process.env.PUBLIC_URL}/images/document-shield-link2.png`} alt="Attachment" />
                    <AttachmentName>{item.name}</AttachmentName>
                  </AttachmentFile>
                  <Icon16 src={`${process.env.PUBLIC_URL}/images/bin.png`} alt="remove attachment" onClick={() => {
                    removeEmailAttachment(item.attachobjid);
                  }} />
                </AttachmentItem>
              )
            })
          }
        </Flex>
      }

      <FormField style={{ width: "100%", marginBottom: 0 }}>
        <FormLabel htmlFor="email_content">Email Content</FormLabel>
        <Editor
          editorState={editorState}
          wrapperClassName="react-draft-wysiwyg-wrapper"
          editorClassName="react-draft-wysiwyg-editor"
          onEditorStateChange={(value) => {
            setEditorState(value);
          }}
        />
      </FormField>
    </Flex >
  );
};

function Email() {
  const { t } = useTranslation();
  const { aid } = useParams();
  const emailUploadRef = useRef(null);
  const { openModalInfo, openModalConfirm, closeModalConfirm } = useModalStore();
  const { data, admSendEmail, admGetPreAuthForm, emailTemplate, openEmailTemplate, setIsValidEmailTemplate, setIsValidEmailTitle, contentComposeEmail_Template } = useAdminApplicationDetailStore();
  const { setLoading } = useLoadingStore();
  const { token, logout } = useAuthAdminStore();

  const loadEmailTemplate = withErrorHandling(openEmailTemplate);
  const sendEmail = withErrorHandling(admSendEmail);
  const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);

  // Email Label
  const getEmailLabel = ({
    title,
    subtitle,
    datetime,
  }) => {
    return (
      <Flex>
        <FormText>{title}</FormText>
        <LogModby>{subtitle}</LogModby>
        <LogDatetime>{datetime}</LogDatetime>
      </Flex>
    )
  }

  const handleFileSelect = async (event, id) => {
    try {
      setLoading(true);
      const file = event.target.files[0];

      const allowedFileTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/gif', 'image/tiff', 'image/tif'];
      const isAllowedFileType = allowedFileTypes.includes(file.type);
      if (!isAllowedFileType) {
        setLoading(false);
        throw new Error('File Type Error');
      }

      const isFileSizeValid = file.size <= 20 * 1024 * 1024; // 20MB in bytes
      if (!isFileSizeValid) {
        setLoading(false);
        throw new Error('File Size Error');
      }

      // console.log("upload file", file, fileList)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('attachid', id);
      formData.append('module', 'PREAUTH');

      const response_upload = await fetch(`${apiDomain}/${basename}/api/general/uploadFile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (response_upload.status === 401) { // Unauthorized
        logout();
        setLoading(false);
        throw new Error('Unauthorized');
      }

      if (!response_upload.ok) {
        setLoading(false);
        throw new Error('Network Error');
      }

      loadAdmGetPreAuthForm(aid);

    } catch (error) {
      if (error.message === "Unauthorized") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_expiry" : "pre_auth_uat_error_expiry",
          title: t("error:unauthorized.title"),
          text: t("error:unauthorized.message")
        });
        // setStep("loading");
      } else if (error.message === "Network Error") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_network" : "pre_auth_uat_error_network",
          title: t("error:network.title"),
          text: t("error:network.message")
        });
      } else if (error.message === "File Type Error") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_file_type" : "pre_auth_uat_error_file_type",
          title: t("error:fileFormat.title"),
          text: t("error:fileFormat.message")
        });
      } else if (error.message === "File Size Error") {
        openModalInfo({
          id: isProduction ? "pre_auth_error_file_size" : "pre_auth_uat_error_file_size",
          title: t("error:fileSize.title"),
          text: t("error:fileSize.message")
        });
      } else {
        openModalInfo({
          id: isProduction ? "pre_auth_error_unknown" : "pre_auth_uat_error_unknown",
          title: t("error:unknown.title"),
          text: t("error:unknown.message")
        });
      }

      setLoading(false);

      return false;
    }
  };

  return (
    <Flex width={"100%"} direction={"column"} alignItems={"flex-start"} style={{ height: "100%", width: "100%", overflow: "auto" }}>
      <Flex>
        <ButtonWithIcon onClick={() => {
          openModalConfirm({
            title: "Compose Email",
            modalConfirmPrimaryButtonText: "Send",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentComposeEmail />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: async () => {
              const { contentComposeEmail_Template, contentComposeEmail_Title } = useAdminApplicationDetailStore.getState();

              let isValid = true;

              if (!contentComposeEmail_Title) {
                setIsValidEmailTitle(false);
                isValid = false;
              }

              if (!contentComposeEmail_Template?.value) {
                setIsValidEmailTemplate(false);
                isValid = false;
              }

              if (isValid) {
                await sendEmail(aid);
                await loadAdmGetPreAuthForm(aid);

                closeModalConfirm();
                openModalInfo({
                  title: "Compose Email",
                  isShowSuccessIcon: true,
                  text: <FormText>Email has been sent to : <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span></FormText>,
                  style: { content: { width: 480 } },
                });
              }
            }
          });

          if (!emailTemplate?.email && contentComposeEmail_Template?.value !== "") {
            loadEmailTemplate(data?.form?.aid, "REQSUP");
          }
        }}>
          <Icon24 src={`${process.env.PUBLIC_URL}/images/email2_white.png`} alt="Compose" />
          <span>Compose</span>
        </ButtonWithIcon>

        <input type="file" style={{ display: 'none' }} onChange={(event) => {
          handleFileSelect(event, data?.form?.emailattachid)
        }} ref={emailUploadRef} />
        <ButtonUpload onClick={() => emailUploadRef.current.click()}>
          <Icon24 src={`${process.env.PUBLIC_URL}/images/document-shield-link.png`} alt="Upload email" />
          <span>Upload Email</span>
        </ButtonUpload>
      </Flex>

      {
        data?.emails?.length === 0 ?
          <EmailEmptyList>
            <ImgEmailNoResult src={`${process.env.PUBLIC_URL}/images/search_no-result.png`} alt="No result" />
            <FormText style={{ color: "var(--aia-bx-dark-blue)" }}>No email record</FormText>
          </EmailEmptyList>
          :
          <EmailList>
            <Collapse
              bordered={false}
              accordion
              defaultActiveKey={['1']}
              style={{
                background: "var(--aia-bx-blue-5)",
                width: "100%",
              }}
              items={data?.emails?.map((item) => {
                if (item.type === "email") {
                  return {
                    key: item.rkey,
                    label: getEmailLabel({
                      title: `Title: ${item.email.subject}`,
                      subtitle: `by ${item.createByUser}`,
                      datetime: item.createdt.replace("T", " ").replace("Z", ""),
                    }),
                    children: <Flex direction={"column"} alignItems={"flex-start"}>

                      {
                        item?.email?.attachment.length > 0 && <Flex gap={16} style={{ flexWrap: "wrap" }}>
                          {
                            item?.email?.attachment?.map((attachment) => {
                              return (
                                <AttachmentItem key={attachment.attachobjid}>
                                  <AttachmentFile href={attachment.orgUrl} target="_blank" rel="noopener noreferrer" download={attachment.name} title={attachment.name}>
                                    <Icon24 src={`${process.env.PUBLIC_URL}/images/document-shield-link2.png`} alt="Attachment" />
                                    <AttachmentName>{attachment.name}</AttachmentName>
                                  </AttachmentFile>
                                </AttachmentItem>
                              )
                            })
                          }
                        </Flex>
                      }

                      <div dangerouslySetInnerHTML={{ __html: item.email.body }} />
                    </Flex>,
                  };
                } else if (item.type === "attachment") {
                  return {
                    key: item.rkey,
                    label: getEmailLabel({
                      title: `Attachment`,
                      subtitle: `by ${item.createByUser}`,
                      datetime: item.createdt.replace("T", " ").replace("Z", ""),
                    }),
                    children: <img src={item.attachment[0].orgUrl} alt={item.name} />,
                  };
                }

                return {};
              })}
            />
          </EmailList>
      }
    </Flex>
  );
}

const ButtonWithIcon = styled(FormButtonPrimary)`
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 3px;
`;

const ButtonUpload = styled(FormButtonSecondary)`
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 3px;
`;

const Icon24 = styled.img`
  width: 24px;
  height: 24px;
`;

const Icon16 = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.7;
  }
`;

const EmailList = styled(Flex)`
  width: 100%;
  min-height: 400px;
  flex-direction: column;
`;

const EmailEmptyList = styled(Flex)`
  width: 100%;
  min-height: 400px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const ImgEmailNoResult = styled.img`
  width: 200px;
  height: 200px;
`;

const LogModby = styled(FormRemark)`
  font-size: 12px;
  line-height: 18px;
`;

const LogDatetime = styled(FormRemark)`
  font-size: 12px;
  line-height: 18px;
`;

const AttachmentBtn = styled(FormButtonSecondary)`
  display: flex;
  align-items: center;
  margin-top: 32px;
  gap: 8px;
`;

const AttachmentItem = styled(Flex)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AttachmentFile = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const AttachmentName = styled(FormText)`
  color: var(--aia-bx-bright-blue-60);
  font-weight: bold;
  text-decoration: underline;
`;

export default Email;